import { css } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { AngularContext } from 'react-app';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import DefaultHeaderBackground from 'athena/components/program-header/default-header-background';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import {
  createMentoringProgramRecruitmentApplication,
  deleteMentoringProgramRecruitmentApplication,
  getCurrentMentoringProgramRecruitmentApplication,
  getMentoringProgramRecruitment,
} from 'redux/actions/mentoring-program-recruitments';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { Recruitment, RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';
import { white } from 'styles/global_defaults/colors';
import { screenMobileMax } from 'styles/global_defaults/media-queries';
import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import FlyerHeader from './flyer-header';
import FlyerInfo from './flyer-info';
import ExpressInterestSuccessModal from './express-interest-success-modal';
import { config } from '@config/pendo.config.json';


const flyerContainerStyles = css`
  position: relative;
  background-color: ${white};
  max-width: 980px;
  width: calc(100% - ${doubleSpacing}px);
  height: fit-content;
  margin: 80px auto ${standardSpacing}px;
  border-radius: ${standardSpacing}px;
`;

const Flyer = () => {
  const dispatch = useAppDispatch();

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { programId } = $state.params || {};
  // Read recruitmentId from the react-router URL so the flyer updates when
  // navigating between recruitments of the same program without a full reload.
  const { recruitmentId: recruitmentIdParam } = useParams<{ recruitmentId: string }>();
  const recruitmentId = Number(recruitmentIdParam);

  const { isAdmin } = useMentoringProgramRole();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { isEnrolled } = mentoringProgram || {};

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentApplication, setCurrentApplication] = useState<RecruitmentApplication | null>(null);
  const [currentRecruitment, setCurrentRecruitment] = useState<Recruitment | null>(null);

  const { isMentee } = useMentoringProgramRole(currentRecruitment?.role?.permission);

  const isMobile = useMediaQuery({
    query: `(max-width: ${screenMobileMax}px)`,
  });

  const formattedInterestDeadline = currentRecruitment?.interestDeadline
    ? moment(currentRecruitment.interestDeadline).format('MMMM D, YYYY')
    : '';

  const isDeadlinePassed = !!currentRecruitment?.interestDeadline
    && moment(currentRecruitment.interestDeadline).isBefore(moment());

  useEffect(() => {
    setCurrentApplication(null);
    wrapThunkAction<RecruitmentApplication | null>(dispatch(getCurrentMentoringProgramRecruitmentApplication({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentApplication(payload);
    });
  }, [dispatch, programId, recruitmentId]);

  useEffect(() => {
    setCurrentRecruitment(null);
    wrapThunkAction<Recruitment>(dispatch(getMentoringProgramRecruitment({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentRecruitment(payload);
    }).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.INFO,
        header: t.MENTORING_PROGRAMS.FLYER.PAGE_UNAVAILABLE.HEADER(),
        message: t.MENTORING_PROGRAMS.FLYER.PAGE_UNAVAILABLE.MESSAGE(),
      }));
      $state.go('dashboard');
    });
  }, [dispatch, programId, recruitmentId]);

  const handleWithdrawInterest = () => {
    wrapThunkAction(dispatch(deleteMentoringProgramRecruitmentApplication({
      programId,
      recruitmentId,
      applicationId: currentApplication?.id,
    }))).then(() => {
      setCurrentApplication(null);
    });
  };

  const handleClick = () => {
    if (isEnrolled) {
      $state.go('mentoring-program-root', { programId });
    } else if (currentApplication) {
      dispatch(openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.MENTORING_PROGRAMS.FLYER.WITHDRAW_INTEREST_WARNING(),
        confirmText: t.MENTORING_PROGRAMS.FLYER.BUTTON.WITHDRAW(),
        onConfirm: handleWithdrawInterest,
      }));
    } else {
      wrapThunkAction<RecruitmentApplication>(dispatch(createMentoringProgramRecruitmentApplication({
        programId,
        recruitmentId,
      }))).then(({ payload }) => {
        setCurrentApplication(payload);
        setShowSuccessModal(true);
      });
    }
  };

  let buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.EXPRESS();
  let buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.expressInterestButton;
  let isButtonDisabled = !!isAdmin;
  if (isEnrolled && !isAdmin) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.ENTER();
    buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.enterProgramButton;
  } else if (isDeadlinePassed) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.CLOSED();
    isButtonDisabled = true;
  } else if (currentApplication && !isAdmin) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.WITHDRAW();
    buttonDataQA = config.pendo.athena.mentorshipProgram.flyer.withdrawInterestButton;
  }

  return (
    <div className='w-100 h-100 position-relative overflow-y-auto pb-5 bg-color-almost-white'>
      <DefaultHeaderBackground
        headerBackground={currentRecruitment?.mentorshipProgram?.headerBackground}
        extraStyles={
          css`
            position: absolute;
            width: 100%;
          `
        }
      />
      <div css={flyerContainerStyles}>
        <FlyerHeader program={currentRecruitment?.mentorshipProgram} isMentee={!!isMentee} />
        <div className={`d-flex justify-content-between p-5 border-top border-gray-6 ${isMobile ? 'flex-column align-items-start' : 'align-items-center'}`}>
          <div>
            <div className='heading-3'>{t.MENTORING_PROGRAMS.FLYER.READY_TO_MAKE_AN_IMPACT()}</div>
            {formattedInterestDeadline && (
              <div className='text-small'>
                {isDeadlinePassed
                  ? t.MENTORING_PROGRAMS.FLYER.INTEREST_DEADLINE.DEADLINE_PASSED(formattedInterestDeadline)
                  : t.MENTORING_PROGRAMS.FLYER.INTEREST_DEADLINE.DEFAULT(formattedInterestDeadline)}
              </div>
            )}
          </div>
          <Button
            variant='primary'
            size='lg'
            className={isMobile ? 'mt-4' : undefined}
            onClick={handleClick}
            disabled={isButtonDisabled}
            data-qa={buttonDataQA}
          >
            {buttonText}
          </Button>
        </div>
        <FlyerInfo recruitment={currentRecruitment} />
        {showSuccessModal && (
          <ExpressInterestSuccessModal
            onClose={() => setShowSuccessModal(false)}
            programName={currentRecruitment?.mentorshipProgram?.name || ''}
          />
        )}
      </div>
    </div>
  );
};

export default Flyer;
